import type { ChatMessages } from "@storytell/cp-client";
import { TbApps, TbChevronRight } from "solid-icons/tb";
import { For, Show, createSignal } from "solid-js";

interface ConceptsRendererProps {
  conceptResults: ChatMessages.ConceptResult;
}

interface ConceptSummaryItemProps {
  concept: ChatMessages.ConceptSummary;
  index: number;
}

const ConceptSummaryItem = (props: ConceptSummaryItemProps) => {
  return (
    <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <div class="flex items-start gap-3">
        {/* Icon container */}
        <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
          <TbApps class="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>

        <div class="flex-1 min-w-0">
          {/* Title with index */}
          <div class="flex items-center gap-2 mb-1">
            <h4 class="font-medium text-sm text-gray-900 dark:text-gray-100 truncate flex-1">{props.concept.name}</h4>
            <span class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded flex-shrink-0">
              #{props.index + 1}
            </span>
          </div>

          {/* Domain if present */}
          <Show when={props.concept.domain}>
            <div class="text-xs text-blue-600 dark:text-blue-400 mb-2">{props.concept.domain}</div>
          </Show>

          {/* Summary in italic if present */}
          <Show when={props.concept.summary}>
            <p class="text-sm text-gray-600 dark:text-gray-400 italic line-clamp-2">{props.concept.summary}</p>
          </Show>

          {/* Description - only show if no summary or if it's different */}
          <Show
            when={
              props.concept.description &&
              (!props.concept.summary || props.concept.summary !== props.concept.description)
            }
          >
            <p class="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 mt-1">
              {props.concept.description.length > 150
                ? `${props.concept.description.substring(0, 150)}...`
                : props.concept.description}
            </p>
          </Show>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders concept results in a structured format with expandable list
 */
export const ConceptsRenderer = (props: ConceptsRendererProps) => {
  const duration = () => {
    const duration = props.conceptResults.duration;
    if (!duration) return "0 seconds";

    let totalSeconds: number;
    if (duration.seconds !== undefined) {
      totalSeconds = Number(duration.seconds) + Number(duration.nanos || 0) / 1000000000;
    } else {
      const durationNum = Number(duration);
      totalSeconds = !Number.isNaN(durationNum) ? durationNum : 0;
    }
    // Round to nearest 0.1
    const rounded = Math.round(totalSeconds * 10) / 10;
    const label = rounded === 1 ? "second" : "seconds";
    return `${rounded} ${label}`;
  };

  const [showConcepts, setShowConcepts] = createSignal(true); // Start expanded for better UX

  return (
    <div>
      <div class="flex items-center gap-1 mb-2">
        <TbApps class="size-4 text-muted-text" />
        <h3 class="text-sm text-secondary-text">Loaded Concepts</h3>
      </div>

      <Show
        when={props.conceptResults.concepts && props.conceptResults.concepts.length > 0}
        fallback={<p class="text-sm text-primary-text italic">No concepts loaded</p>}
      >
        <button
          type="button"
          onClick={() => setShowConcepts((prev) => !prev)}
          class="group/btn flex items-center gap-2 mb-2 cursor-pointer"
        >
          <p class="text-sm text-blue-800 dark:text-blue-400 font-medium group-hover/btn:underline">
            Loaded {props.conceptResults.totalConcepts || props.conceptResults.concepts.length} concept
            {(props.conceptResults.totalConcepts || props.conceptResults.concepts.length) !== 1 ? "s" : ""} in{" "}
            {duration()}
          </p>

          <TbChevronRight
            class="size-3.5 stroke-3 text-secondary-text transition-transform"
            classList={{ "rotate-90": showConcepts() }}
          />
        </button>

        <Show when={showConcepts()}>
          <div class="space-y-3">
            <For each={props.conceptResults.concepts}>
              {(concept, index) => <ConceptSummaryItem concept={concept} index={index()} />}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};
